import axios, { AxiosError } from 'axios';
import { useAuthStore } from '../store/authStore';
import {
  mockAuthAPI,
  mockContactsAPI,
  mockInteractionsAPI,
  mockLineupAPI,
} from './mockApi';

// Use mock API in development when no API URL is configured
const API_URL = import.meta.env.VITE_API_URL || '';
const USE_MOCK = import.meta.env.DEV && !API_URL;

// Create axios instance
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Avoid redirect loop - don't redirect if already on login or callback pages
      const currentPath = window.location.pathname;
      const isAuthPage = currentPath.startsWith('/login') ||
        currentPath.startsWith('/auth/') ||
        currentPath.startsWith('/oauth-callback');

      if (!isAuthPage) {
        // Clear auth state and redirect to login
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============================================
// Auth API
// ============================================

const realAuthAPI = {
  loginWithGoogle: async (redirectUri: string) => {
    const { data } = await api.post('/auth/login/google', { redirect_uri: redirectUri });
    return data;
  },

  handleCallback: async (code: string, state: string) => {
    const { data } = await api.post('/auth/callback/google', { code, state });
    return data;
  },

  logout: async () => {
    const { data } = await api.post('/auth/logout');
    return data;
  },

  getMe: async () => {
    const { data } = await api.get('/auth/me');
    return data;
  },

  refreshToken: async () => {
    const { data } = await api.post('/auth/refresh');
    return data;
  },

  // Magic Link authentication
  sendMagicLink: async (email: string) => {
    const { data } = await api.post('/auth/magic-link', { email });
    return data as {
      success: boolean;
      data?: { message: string };
      error?: { code: string; message: string };
    };
  },

  verifyMagicLink: async (token: string) => {
    const { data } = await api.post('/auth/magic-link/verify', { token });
    return data as {
      success: boolean;
      data?: {
        token: string;
        user: {
          id: string;
          email: string;
          name: string;
          plan_tier: string;
          team_id?: string;
        };
        is_new_user: boolean;
      };
      error?: { code: string; message: string };
    };
  },
};

export const authAPI = USE_MOCK ? mockAuthAPI : realAuthAPI;

// ============================================
// Contacts API
// ============================================

export interface Contact {
  id: string;
  first_name: string;
  last_name?: string;
  email?: string;
  phone?: string;
  stage: string;
  tags?: string[];
  contact_type?: string;
  company?: string;
  last_contact_at?: number;
  next_contact_date?: string;
  ai_summary?: string;
  created_at: number;
}

export interface ContactsParams {
  stage?: string;
  search?: string;
  tags?: string;
  contact_type?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  limit?: number;
  cursor?: string;
  filter?: 'contact_today' | 'overdue' | 'in_progress' | string | null;
}

const realContactsAPI = {
  list: async (params?: ContactsParams) => {
    const { data } = await api.get('/contacts', { params });
    return data;
  },

  get: async (id: string) => {
    const { data } = await api.get(`/contacts/${id}`);
    return data;
  },

  create: async (contact: Partial<Contact> | Record<string, unknown>) => {
    const { data } = await api.post('/contacts', contact);
    return data;
  },

  update: async (id: string, updates: Partial<Contact> | Record<string, unknown>) => {
    const { data } = await api.patch(`/contacts/${id}`, updates);
    return data;
  },

  delete: async (id: string, permanent = false) => {
    const { data } = await api.delete(`/contacts/${id}`, {
      params: { permanent: permanent ? 'true' : undefined },
    });
    return data;
  },

  restore: async (id: string) => {
    const { data } = await api.post(`/contacts/${id}/restore`);
    return data;
  },

  getStats: async () => {
    const { data } = await api.get('/contacts/stats');
    return data;
  },
};

export const contactsAPI = USE_MOCK ? mockContactsAPI : realContactsAPI;

// ============================================
// Interactions API
// ============================================

export interface Interaction {
  id: string;
  contact_id: string;
  type: 'call' | 'email' | 'sms' | 'meeting' | 'note' | 'task';
  direction?: 'inbound' | 'outbound';
  channel: string;
  subject?: string;
  body?: string;
  status: string;
  occurred_at: number;
}

const realInteractionsAPI = {
  list: async (params?: {
    contact_id?: string;
    type?: string;
    limit?: number;
    cursor?: string;
  }) => {
    const { data } = await api.get('/interactions', { params });
    return data;
  },

  create: async (interaction: Partial<Interaction>) => {
    const { data } = await api.post('/interactions', interaction);
    return data;
  },

  get: async (id: string) => {
    const { data } = await api.get(`/interactions/${id}`);
    return data;
  },

  update: async (id: string, interaction: Partial<Interaction>) => {
    const { data } = await api.put(`/interactions/${id}`, interaction);
    return data;
  },

  delete: async (id: string) => {
    const { data } = await api.delete(`/interactions/${id}`);
    return data;
  },
};

export const interactionsAPI = USE_MOCK ? mockInteractionsAPI : realInteractionsAPI;

// ============================================
// Lineup API
// ============================================

export interface DailyPickContact {
  contact_id: string;
  reason: string;
  best_channel: 'call' | 'email' | 'text';
  best_time_window: 'morning' | 'afternoon' | 'evening';
  engagement_signals: string[];
  contact?: Contact;
}

export interface PriorityContact {
  contact_id: string;
  priority_rank: number;
  priority_level: 'urgent' | 'high' | 'medium' | 'low';
  reason: string;
  context_summary: string;
  suggested_action: {
    type: string;
    message?: string;
    best_time?: string;
  };
  contact?: Contact;
}

export interface Lineup {
  id: string;
  date: string;
  daily_pick: DailyPickContact[];
  priority_contacts: PriorityContact[];
  pipeline_health?: {
    total_contacts: number;
    stage_breakdown: Record<string, number>;
    at_risk_count: number;
    overdue_count: number;
  };
  total_contacts_analyzed: number;
  generated_at: number;
  actions_taken: number;
}

const realLineupAPI = {
  getToday: async (refresh = false) => {
    const { data } = await api.get('/lineup/today', {
      params: { refresh: refresh ? 'true' : undefined },
    });
    return data;
  },

  getByDate: async (date: string) => {
    const { data } = await api.get(`/lineup/${date}`);
    return data;
  },

  getHistory: async (limit = 7) => {
    const { data } = await api.get('/lineup/history', { params: { limit } });
    return data;
  },

  recordAction: async (lineupId: string, contactId: string, actionType: string) => {
    const { data } = await api.post(`/lineup/${lineupId}/action`, {
      contact_id: contactId,
      action_type: actionType,
    });
    return data;
  },

  markOpened: async () => {
    const { data } = await api.post('/lineup/today/mark-opened');
    return data;
  },

  getPreferences: async () => {
    const { data } = await api.get('/lineup/preferences');
    return data;
  },

  updatePreferences: async (preferences: Partial<LineupPreferences>) => {
    const { data } = await api.put('/lineup/preferences', preferences);
    return data;
  },
};

// Lineup preferences type
export interface LineupPreferences {
  max_contacts: number;
  include_hall_of_fame: boolean;
  include_bench: boolean;
  focus_stages: string[];
  minimum_days_between_contact: number;
  prioritize_upcoming_dates: boolean;
  prioritize_high_value: boolean;
  preferred_generation_time: string;
}

export const lineupAPI = USE_MOCK ? mockLineupAPI : realLineupAPI;

// ============================================
// Tags API
// ============================================

export interface Tag {
  id: string;
  name: string;
  slug: string;
  color: string;
  ai_description?: string;
  ai_instructions?: string;
  contact_count: number;
  created_at: number;
  updated_at: number;
}

export interface CreateTagInput {
  name: string;
  color?: string;
  ai_description?: string;
  ai_instructions?: string;
}

export interface UpdateTagInput {
  name?: string;
  color?: string;
  ai_description?: string | null;
  ai_instructions?: string | null;
}

export const tagsAPI = {
  list: async () => {
    const { data } = await api.get('/tags');
    return data;
  },

  get: async (id: string) => {
    const { data } = await api.get(`/tags/${id}`);
    return data;
  },

  create: async (tag: CreateTagInput) => {
    const { data } = await api.post('/tags', tag);
    return data;
  },

  update: async (id: string, updates: UpdateTagInput) => {
    const { data } = await api.patch(`/tags/${id}`, updates);
    return data;
  },

  delete: async (id: string) => {
    const { data } = await api.delete(`/tags/${id}`);
    return data;
  },

  // Contact-tag relationships
  addToContact: async (tagId: string, contactId: string) => {
    const { data } = await api.post(`/tags/${tagId}/contacts/${contactId}`);
    return data;
  },

  removeFromContact: async (tagId: string, contactId: string) => {
    const { data } = await api.delete(`/tags/${tagId}/contacts/${contactId}`);
    return data;
  },

  getContacts: async (tagId: string, params?: { limit?: number; cursor?: string }) => {
    const { data } = await api.get(`/tags/${tagId}/contacts`, { params });
    return data;
  },
};

// ============================================
// CSV Import API
// ============================================

export interface CSVColumnMapping {
  csv_column: string;
  csv_column_index: number;
  maps_to: string[];
  transformation: 'direct' | 'parse_name' | 'parse_address' | 'normalize_phone' | 'extract_dates' | 'map_stage';
  confidence: 'high' | 'medium' | 'low';
  examples: {
    input: string;
    output: Record<string, string>;
  }[];
}

export interface CSVAnalysisResult {
  mappings: CSVColumnMapping[];
  warnings: string[];
  quality_score: number;
}

export const csvAPI = {
  analyze: async (headers: string[], sampleRows: string[][], useAI = true) => {
    const { data } = await api.post('/csv/analyze', {
      headers,
      sample_rows: sampleRows,
      use_ai: useAI,
    });
    return data as {
      success: boolean;
      data: {
        analysis: CSVAnalysisResult;
        ai_used: boolean;
        warning?: string;
      };
    };
  },

  transform: async (rows: string[][], mappings: CSVColumnMapping[]) => {
    const { data } = await api.post('/csv/transform', {
      rows,
      mappings,
    });
    return data as {
      success: boolean;
      data: {
        contacts: Record<string, string>[];
        total: number;
        skipped: number;
      };
    };
  },
};

// ============================================
// Google Contacts API
// ============================================

export interface GoogleContactsImportResult {
  total: number;
  imported: number;
  skipped: number;
  duplicates: number;
  errors: string[];
}

export const googleContactsAPI = {
  getCount: async () => {
    const { data } = await api.get('/google-contacts/count');
    return data as {
      success: boolean;
      data?: { count: number };
      error?: { code: string; message: string };
    };
  },

  import: async () => {
    const { data } = await api.post('/google-contacts/import');
    return data as {
      success: boolean;
      data?: GoogleContactsImportResult;
      error?: { code: string; message: string };
    };
  },

  getStatus: async () => {
    const { data } = await api.get('/google-contacts/status');
    return data as {
      success: boolean;
      data?: { status: 'idle' | 'in_progress' };
    };
  },
};

// ============================================
// Team API
// ============================================

export interface TeamStats {
  total_contacts: number;
  active_deals: number;
  avg_response_time: number;
  weekly_touchpoints: number;
  pipeline_value: number;
  conversion_rate: number;
  contacts_needing_attention: number;
}

export interface AgentStats {
  user_id: string;
  name: string;
  avatar_url: string | null;
  contacts_count: number;
  touches_this_week: number;
  avg_touches_per_week: number;
  active_deals: number;
  pipeline_value: number;
  conversion_rate: number;
  deals_closed_this_month: number;
  status: 'high-performer' | 'on-track' | 'needs-attention';
  trend: 'up' | 'down' | 'stable';
}

export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  name: string;
  avatar_url: string | null;
  score: number;
  metric_label: string;
  change: number;
}

export interface MyStats {
  user_id: string;
  name: string;
  avatar_url: string | null;
  team_id: string | null;
  stats: {
    total_contacts: number;
    interactions_this_week: number;
    total_interactions: number;
    active_deals: number;
    deals_closed_this_month: number;
    activity_days_last_30: number;
  };
}

export const teamAPI = {
  getStats: async (period = 'week') => {
    const { data } = await api.get('/team/stats', { params: { period } });
    return data as {
      success: boolean;
      data?: { stats: TeamStats; team_id: string | null };
    };
  },

  getAgents: async (period = 'week') => {
    const { data } = await api.get('/team/agents', { params: { period } });
    return data as {
      success: boolean;
      data?: { agents: AgentStats[]; team_id: string | null };
    };
  },

  getLeaderboard: async (metric = 'interactions', period = 'week') => {
    const { data } = await api.get('/team/leaderboard', { params: { metric, period } });
    return data as {
      success: boolean;
      data?: {
        leaderboard: LeaderboardEntry[];
        metric: string;
        period: string;
        team_id: string | null;
      };
    };
  },

  getMyStats: async () => {
    const { data } = await api.get('/team/my-stats');
    return data as {
      success: boolean;
      data?: MyStats;
    };
  },

  getRisingStars: async () => {
    const { data } = await api.get('/team/rising-stars');
    return data as {
      success: boolean;
      data?: {
        rising_stars: Array<{
          rank: number;
          user_id: string;
          name: string;
          avatar_url: string | null;
          this_week: number;
          last_week: number;
          improvement: number;
          improvement_percent: number;
        }>;
        team_id: string | null;
      };
    };
  },
};

// ============================================
// Campaigns API Types
// ============================================

export interface CampaignObjective {
  metric: string;
  target: number;
  label: string;
}

export interface Campaign {
  id: string;
  team_id: string;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  objectives: CampaignObjective[];
  is_active: number;
  is_team_goal: number;
  created_by: string;
  creator_name?: string;
  created_at: number;
  updated_at: number;
  participant_count?: number;
  is_participating?: boolean;
}

export interface CampaignParticipant {
  user_id: string;
  user_name: string;
  avatar_url: string | null;
  current_scores: Record<string, number>;
  progress_percent: number;
  current_rank: number | null;
  previous_rank: number | null;
  joined_at: number;
}

export interface CreateCampaignInput {
  name: string;
  description?: string;
  start_date: string;
  end_date: string;
  objectives: CampaignObjective[];
  is_team_goal?: boolean;
}

// ============================================
// Support API
// ============================================

export interface SupportRequest {
  subject: 'feature' | 'account' | 'billing' | 'other';
  message: string;
}

export interface BugReport {
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  page: string;
  steps: string;
  expected?: string;
  actual?: string;
  browser?: string;
}

export const supportAPI = {
  submitRequest: async (request: SupportRequest) => {
    const { data } = await api.post('/support/request', request);
    return data as {
      success: boolean;
      data?: { id: string; message: string };
      error?: { code: string; message: string; details?: unknown };
    };
  },

  submitBugReport: async (report: BugReport) => {
    const { data } = await api.post('/support/bug', report);
    return data as {
      success: boolean;
      data?: { id: string; message: string };
      error?: { code: string; message: string; details?: unknown };
    };
  },
};

// ============================================
// Campaigns API
// ============================================

export const campaignsAPI = {
  list: async (status = 'all') => {
    const { data } = await api.get('/campaigns', { params: { status } });
    return data as {
      success: boolean;
      data?: { campaigns: Campaign[] };
    };
  },

  get: async (id: string) => {
    const { data } = await api.get(`/campaigns/${id}`);
    return data as {
      success: boolean;
      data?: {
        campaign: Campaign;
        participants: CampaignParticipant[];
        is_participating: boolean;
      };
    };
  },

  create: async (input: CreateCampaignInput) => {
    const { data } = await api.post('/campaigns', input);
    return data as {
      success: boolean;
      data?: { id: string; message: string };
      error?: string;
    };
  },

  update: async (id: string, input: Partial<CreateCampaignInput> & { is_active?: boolean }) => {
    const { data } = await api.put(`/campaigns/${id}`, input);
    return data as {
      success: boolean;
      message?: string;
      error?: string;
    };
  },

  delete: async (id: string) => {
    const { data } = await api.delete(`/campaigns/${id}`);
    return data as {
      success: boolean;
      message?: string;
      error?: string;
    };
  },

  join: async (id: string) => {
    const { data } = await api.post(`/campaigns/${id}/join`);
    return data as {
      success: boolean;
      message?: string;
      error?: string;
    };
  },

  leave: async (id: string) => {
    const { data } = await api.post(`/campaigns/${id}/leave`);
    return data as {
      success: boolean;
      message?: string;
      error?: string;
    };
  },

  getLeaderboard: async (id: string) => {
    const { data } = await api.get(`/campaigns/${id}/leaderboard`);
    return data as {
      success: boolean;
      data?: {
        leaderboard: Array<{
          rank: number;
          user_id: string;
          name: string;
          avatar_url: string | null;
          scores: Record<string, number>;
          total_score: number;
          progress_percent: number;
        }>;
        objectives: CampaignObjective[];
      };
    };
  },
};

// ============================================
// Messages API (AI Drafting)
// ============================================

export type MessageType = 'check_in' | 'follow_up' | 'thank_you' | 'introduction' | 'update' | 'market_update' | 'birthday' | 'anniversary' | 'referral_ask' | 'just_listed' | 'custom';
export type MessageChannel = 'email' | 'sms' | 'letter';

export interface DraftMessageInput {
  contact_id: string;
  message_type: MessageType;
  channel: MessageChannel;
  context?: string; // Additional context from user (custom prompt)
}

export interface DraftMessageResponse {
  draft: string;
  subject: string | null;
  suggestions: string[];
}

export const messagesAPI = {
  draftMessage: async (input: DraftMessageInput) => {
    const { data } = await api.post('/messages/draft-message', input);
    return data as {
      success: boolean;
      data?: DraftMessageResponse;
      error?: { code: string; message: string; details?: unknown };
    };
  },
};

// ============================================
// Templates API
// ============================================

export interface Template {
  id: string;
  user_id: string | null;
  team_id: string | null;
  name: string;
  description: string | null;
  category: 'check_in' | 'follow_up' | 'thank_you' | 'introduction' | 'update' | 'market_update' | 'birthday' | 'anniversary' | 'referral_ask' | 'just_listed' | 'custom' | 'other';
  channel: 'email' | 'sms' | 'letter';
  subject: string | null;
  body: string;
  body_html: string | null;
  variables: string[];
  is_shared: number;
  is_locked: number;
  times_used: number;
  is_active: number;
  created_at: number;
  updated_at: number;
}

export interface CreateTemplateInput {
  name: string;
  description?: string;
  category?: Template['category'];
  channel?: Template['channel'];
  subject?: string;
  body: string;
  body_html?: string;
  is_shared?: boolean;
}

export interface UpdateTemplateInput {
  name?: string;
  description?: string | null;
  category?: Template['category'];
  channel?: Template['channel'];
  subject?: string | null;
  body?: string;
  body_html?: string | null;
  is_shared?: boolean;
  is_active?: boolean;
}

export const templatesAPI = {
  list: async (filters?: { category?: string; channel?: string; is_shared?: boolean }) => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.channel) params.append('channel', filters.channel);
    if (filters?.is_shared !== undefined) params.append('is_shared', filters.is_shared.toString());
    
    const queryString = params.toString();
    const { data } = await api.get(`/templates${queryString ? '?' + queryString : ''}`);
    return data.data.templates as Template[];
  },

  get: async (id: string) => {
    const { data } = await api.get(`/templates/${id}`);
    return data.data.template as Template;
  },

  create: async (template: CreateTemplateInput) => {
    const { data } = await api.post('/templates', template);
    return data.data.template as Template;
  },

  update: async (id: string, template: UpdateTemplateInput) => {
    const { data } = await api.patch(`/templates/${id}`, template);
    return data.data.template as Template;
  },

  delete: async (id: string) => {
    const { data } = await api.delete(`/templates/${id}`);
    return data;
  },

  incrementUsage: async (id: string) => {
    const { data } = await api.post(`/templates/${id}/increment-usage`);
    return data;
  },
};

// ============================================
// User Preferences API
// ============================================

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  work_hours: {
    startHour: number;
    startMinute: number;
    endHour: number;
    endMinute: number;
    workDays: number[];
  };
  notification_preferences: {
    email_daily_lineup: boolean;
    email_weekly_summary: boolean;
    push_enabled: boolean;
    contact_reminders: boolean;
    marketing_emails: boolean;
  };
  ai_preferences: {
    enable_ai_features: boolean;
    ai_csv_processing: boolean;
    ai_message_drafting: boolean;
  };
  communication_preferences: {
    preferred_messaging_app: 'apple_messages' | 'google_messages' | 'google_voice' | 'default';
    use_browser_email: boolean;
  };
}

export const preferencesAPI = {
  get: async () => {
    const { data } = await api.get('/preferences');
    return data as {
      success: boolean;
      data?: { preferences: UserPreferences };
      error?: { code: string; message: string };
    };
  },

  update: async (preferences: Partial<UserPreferences>) => {
    const { data } = await api.put('/preferences', preferences);
    return data as {
      success: boolean;
      data?: { preferences: UserPreferences };
      error?: { code: string; message: string };
    };
  },
};
